/**
 * The wiring every token-gated query in this app shares, in one place so it cannot be got wrong
 * in seventeen.
 *
 * What was unobservable before this module existed: the object handed to `useQuery` was built
 * inline inside a hook body, so the query key, the `enabled` flag and the not-signed-in guard
 * could only be reached by rendering a React tree. This app has no renderer, and 14-02 recorded
 * the consequence — "a hook that fires with `enabled: true` and no token, or two hooks sharing a
 * cache key, would be invisible today". Nothing about those three values needs React: they are a
 * plain object, and a factory makes them directly callable.
 *
 * Three things that were three independent chances to be wrong are now one decision:
 *
 *   - the token is appended to the key, so two accounts never share a cache slot. A key without
 *     it serves the previous user's team, captures and billing status to whoever signs in next
 *     on the same machine;
 *   - `enabled` is `!!token`, so a query cannot fire without one. A query that fires anyway
 *     sends `Authorization: Bearer ` and the failure is charged to the user as a broken page;
 *   - `queryFn` refuses rather than calling with an empty token, because `enabled` is a
 *     scheduling hint and `refetch()` ignores it.
 *
 * It deliberately knows nothing about: which endpoint is called, what the response means, how a
 * failure is displayed, and how long anything is cached.
 */
import type { QueryKey } from "@tanstack/react-query";

/** Exactly the three properties this app's queries set — nothing here configures caching. */
export interface TokenQueryOptions<T> {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  enabled: boolean;
}

/**
 * Builds the options for one token-gated query.
 *
 * `prefix` is the hook's own identity and must be unique across the app; `query-wiring.test.ts`
 * is what enforces that, since a collision is otherwise silent — the second hook simply reads
 * the first one's cached data.
 */
export function tokenQuery<T>(
  prefix: readonly unknown[],
  token: string | null,
  run: (token: string) => Promise<T>,
): TokenQueryOptions<T> {
  return {
    queryKey: [...prefix, token ?? ""],
    queryFn: async () => {
      if (!token) throw new Error("Nicht angemeldet");
      return run(token);
    },
    enabled: !!token,
  };
}
